import sileo from 'sileo';

const EmploymentPolicy = new sileo.Model('bposeats', 'employment-policy-choices');
const EmploymentRule = new sileo.Model('bposeats', 'employment-rule-choices');
const ScheduleTemplate = new sileo.Model('bposeats', 'schedule-choices');
const OnboardEmployee = new sileo.Model('bposeats', 'employer-onboard-employees');
const DepartmentsInCampaign = new sileo.Model('bposeats', 'department-choices');
const UserFilter = new sileo.Model('users', 'users');
const UserProfileFilter = new sileo.Model('users', 'user_profile');
const UpdateUserProfile = new sileo.Model('hqzen', 'update-onboarding-applicant-profile');
const EmailApplicants = new sileo.Model('hqzen', 'email-applicants-without-account');
const EmploymentOnboardingTourGuide = new sileo.Model('hqzen', 'employment-onboarding-tour-guide');

/**
 * @typedef {Object} Schedule
 * @property {number} pk
 * @property {string} name
 * @property {boolean} is_flexible
 */

const state = {
    totalCampaigns: 0,
    employmentPolicy: {
        'pk': -1,
        'name': 'Select an Employment Policy'
    },

    /**
     * The work schedule for the onboarded employees.
     *
     * @type {Schedule | null}
     */
    workSchedule: null,

    onboardingContractDetails: {
        'pk': -1,
        'name': '',
        'contents': null
    },

    employeeRule: {
        allowOvertime: false,
        afterOTRequest: false,
        autoOvertime: false,
        overtimeRate: 125,
    },
    currency: localStorage.getItem('currency') || 'USD',
    personal: {
        firstName: '',
        lastName: '',
        email: '',
        phoneCode: '+63',
        phoneNumber: '',
        birthDate: '',
        sex: 'Male'
    },
    employment: {
        position: '',
        role: 'Rank and File',
        department: '',
        team: '',
        payableType: 'Wage',
        salary: '',
        startDate: '',
        allowedPlatform: ''
    },
    onboardingError: '',
    platformChoices: [
        'Both Mobile and Desktop',
        'Desktop Only',
        'Mobile Only'
    ],
    csvFileName: '',
    payload: null,
    finalSalaryValueUSD: null,

    /**
     * This is also known as `payable_type` in the backend.
     *
     * @type {'Wage' | 'Salary'}
     */
    compensationType: 'Wage',

    /**
     * The list of employees taken from the CSV file.
     *
     * @type {Object[]}
     */
    employees: [],

    /**
     * The ID of the selected company under which the new employees will be
     * onboarded.
     *
     * @type {number | null}
     */
    companyId: null,
};

const getters = {
    employmentPolicy: state => state.employmentPolicy,
    workSchedule: state => state.workSchedule,
    employeeRule: state => state.employeeRule,
    personal: state => state.personal,
    employment: state => state.employment,
    onboardingError: state => state.onboardingError,
    platformChoices: state => state.platformChoices,
    csvFileName: state => state.csvFileName,
    compensationType: state => state.compensationType,
    currency: state => state.currency,
    employees: state => state.employees,
    companyId: state => state.companyId,
    onboardingContractDetails: state => state.onboardingContractDetails,
    finalSalaryValueUSD: state => state.finalSalaryValueUSD,
    payload: (state, _getters, rootState) => {
        return {
            'company_id': state.companyId,
            'workforce_id': rootState.workforce.selectedWorkforce.pk,
            'employment_policy_id': state.employmentPolicy.pk,
            'can_file_overtime_after_shift': state.employeeRule.afterOTRequest,
            'can_overtime': state.employeeRule.allowOvertime,
            'overtime_rate': state.employeeRule.overtimeRate / 100,
            'auto_overtime': state.employeeRule.autoOvertime,
            'employees': JSON.stringify(state.employees),
        };
    },
};

const mutations = {
    setTotalCampaigns(state, totalCampaigns) {
        state.totalCampaigns = totalCampaigns;
    },

    updateEmploymentPolicy(state, employmentPolicy) {
        state.employmentPolicy = employmentPolicy;
    },

    updateCurrency(state, currency) {
        state.currency = currency;
    },

    updatePayload(state, payload) {
        state.payload = payload;
    },

    /**
     * Updates the work schedule.
     *
     * @param {*} state - The Vuex store state
     * @param {Schedule | null} workSchedule - The work schedule
     */
    updateWorkSchedule(state, workSchedule) {
        state.workSchedule = workSchedule;
    },
    updateFinalSalaryValueUSD(state, salaryUSDValue) {
        state.finalSalaryValueUSD = salaryUSDValue;
    },

    updateOnboardingContractDetails(state, contractDetails) {
        state.onboardingContractDetails = contractDetails;
    },

    resetOnboarding(state) {
        state.personal = {
            firstName: '',
            lastName: '',
            email: '',
            phoneCode: '+63',
            phoneNumber: '',
            birthDate: '',
            sex: 'Male'
        };

        state.employment = {
            position: '',
            role: 'Rank and File',
            department: '',
            team: '',
            payableType: 'Wage',
            salary: '',
            startDate: ''
        };
        state.workSchedule = null;

        state.finalSalaryValueUSD = null;

        state.payload = null;

        state.onboardingContractDetails = {
            'name': '',
            'contents': null
        };
    },
    setOnboardingError(state, payload) {
        state.onboardingError = payload;
    },

    updateCSVFileName(state, fileName) {
        state.csvFileName = fileName;
    },

    /**
     * Changes the compensation type to the given type.
     *
     * @param {*} state - The Vuex store state
     * @param {'Wage' | 'Salary'} type - The compensation type
     */
    updateCompensationType(state, type) {
        state.compensationType = type;
    },

    /**
     * Updates the list of employees.
     *
     * @param {*} state - The Vuex store state
     * @param {Object[]} employees - The new list of employees
     */
    updateEmployees(state, employees) {
        state.employees = employees;
    },

    /**
     * Updates the selected company.
     *
     * @param {*} state - The Vuex store state
     * @param {number} companyId - The company ID
     */
    updateCompanyId(state, companyId) {
        state.companyId = companyId;
    },
};

const actions = {
    createBulkOnboarding(context, payload) {
        return OnboardEmployee.objects.create(payload);
    },

    emailApplicantsWithoutAccount(context, data) {
        return EmailApplicants.objects.update(data);
    },

    onboardSingleEmployee(context, data) {
        let formData = new FormData();
        for (let key in data) {
            formData.append(key, data[key]);
        }

        return OnboardEmployee.objects.create(formData).then(response => {
            return response;
        }).catch(response => {
            return JSON.parse(response.response);
        });
    },

    updateOnboardApplicantEmail(context, data) {
        let filter = {
            email: data.oldEmail,
            employer: data.employer.pk,
            company: data.company
        };
        let formData = new FormData();
        formData.append('email', data.newEmail);
        return UpdateUserProfile.objects.update(filter, formData);
    },

    fetchEmploymentPolicy(context, filters) {
        return EmploymentPolicy.objects.filter(filters);
    },

    fetchEmploymentRule(context, filters) {
        return EmploymentRule.objects.filter(filters);
    },

    fetchScheduleTemplate(context, filters) {
        return ScheduleTemplate.objects.filter(filters);
    },

    clearOnBoardingError({ commit }) {
        commit('setOnboardingError', '');
    },

    fetchDepartments(context, filters) {
        return DepartmentsInCampaign.objects.filter(filters);
    },

    fetchUsers(context, filters) {
        return UserFilter.objects.filter(filters);
    },

    fetchUserProfile(context, filters) {
        return UserProfileFilter.objects.filter(filters);
    },

    fetchEmploymentOnboardingTourGuide(context, pk) {
        return EmploymentOnboardingTourGuide.objects.get(pk);
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
